if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector('[data-bundle-config]');
      if (!cfgEl) return;
      this.cfg = JSON.parse(cfgEl.textContent);
      this.str = this.cfg.strings || {};
      this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      this.designMode = !!(window.Shopify && Shopify.designMode);

      this.tiers = (this.cfg.tiers || []).filter((t) => t.quantity > 0).sort((a, b) => a.quantity - b.quantity);
      this.maxTierQty = this.tiers.length ? this.tiers[this.tiers.length - 1].quantity : 1;

      this.rows = [...this.querySelectorAll('[data-bundle-row]')];

      this.els = {
        track: this.querySelector('[data-bundle-track]'),
        fill: this.querySelector('[data-bundle-fill]'),
        ticks: [...this.querySelectorAll('[data-bundle-tick]')],
        pcts: [...this.querySelectorAll('[data-bundle-pct]')],
        status: this.querySelector('[data-bundle-status]'),
        items: this.querySelector('[data-bundle-items]'),
        skeleton: this.querySelector('[data-bundle-skeleton]'),
        totals: this.querySelector('[data-bundle-totals]'),
        totalAmount: this.querySelector('[data-bundle-total-amount]'),
        savings: this.querySelector('[data-bundle-savings]'),
        savingsAmount: this.querySelector('[data-bundle-savings-amount]'),
        cta: this.querySelector('[data-bundle-cta]'),
        alert: this.querySelector('[data-bundle-alert]'),
      };

      this.stickyMobile = this.dataset.stickyMobile === 'true';
      if (this.stickyMobile) {
        this.els.mobilebar = this.querySelector('[data-bundle-mobilebar]');
        this.els.mobilebarFill = this.querySelector('[data-bundle-mobilebar-fill]');
        this.els.mobilebarHint = this.querySelector('[data-bundle-mobilebar-hint]');
        this.els.backdrop = this.querySelector('[data-bundle-backdrop]');
        this.els.sheetClose = this.querySelector('[data-bundle-sheet-close]');
        this.els.panel = this.querySelector('.bundle-builder__panel');
      }

      this.parseVariants();
      this.positionMeter();
      this.measureAddHeight();
      requestAnimationFrame(() => this.measureAddHeight());
      this.bindControls();
      this.els.cta.addEventListener('click', () => this.addBundle());
      this._onResize = () => this.measureAddHeight();
      window.addEventListener('resize', this._onResize);

      if (this.stickyMobile && this.els.mobilebar) {
        // Portal the fixed elements to <body> so ancestor transforms (the theme's
        // scroll-reveal / page-transition animations create a containing block) can't
        // turn position:fixed into absolute positioning. The panel is portaled only
        // while the sheet is open (see openSheet), so its desktop rail stays intact.
        this._portal = document.createElement('div');
        this._portal.className = 'bundle-builder__portal';
        if (this.els.backdrop) this._portal.appendChild(this.els.backdrop);
        this._portal.appendChild(this.els.mobilebar);
        document.body.appendChild(this._portal);
        if (this.els.panel) this._panelHome = { parent: this.els.panel.parentNode, next: this.els.panel.nextSibling };

        this.classList.add('bb-sticky-ready');
        this.measureBarHeight();
        requestAnimationFrame(() => this.measureBarHeight());
        this._onBarResize = () => { this.measureBarHeight(); this._barDocked = null; this.updateBarDock(); };
        window.addEventListener('resize', this._onBarResize);

        this.els.mobilebar.addEventListener('click', () => this.toggleSheet());
        if (this.els.sheetClose) this.els.sheetClose.addEventListener('click', () => this.closeSheet());
        if (this.els.backdrop) this.els.backdrop.addEventListener('click', () => this.closeSheet());
        this._onKeydown = (e) => { if (e.key === 'Escape' && this._portal.classList.contains('is-sheet-open')) this.closeSheet(); };
        document.addEventListener('keydown', this._onKeydown);

        this.setupBarObserver();

        this._onScroll = () => {
          if (this._dockRaf) return;
          this._dockRaf = requestAnimationFrame(() => { this._dockRaf = null; this.updateBarDock(); });
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }

      this.restoreState();
      this.render();
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onBarResize) window.removeEventListener('resize', this._onBarResize);
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._dockRaf) cancelAnimationFrame(this._dockRaf);
      if (this._barObserver) this._barObserver.disconnect();
      if (this._panelReturnT) clearTimeout(this._panelReturnT);
      if (this._portal) this._portal.remove();
      document.body.classList.remove('bb-sheet-lock');
    }

    // ---- helpers ----
    t(str, vars = {}) {
      return String(str || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ''));
    }
    money(cents) {
      const amount = (cents / 100).toFixed(2);
      return (this.cfg.moneyFormat || '${{amount}}')
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100));
    }
    qtyOf(row) { return Number(row.dataset.qty || 0); }
    totalQty() { return this.rows.reduce((s, r) => s + this.qtyOf(r), 0); }
    tierFor(qty) {
      let hit = null;
      for (const tr of this.tiers) if (qty >= tr.quantity) hit = tr;
      return hit;
    }

    parseVariants() {
      this.rows.forEach((row) => {
        const el = row.querySelector('[data-bundle-variants]');
        if (!el) return;
        try { row._variants = JSON.parse(el.textContent); } catch (e) { row._variants = null; }
      });
    }

    positionMeter() {
      this.els.ticks.forEach((d) => {
        const pct = Math.min(100, (Number(d.dataset.qty) / this.maxTierQty) * 100);
        // The final milestone sits at the bar's end, where a tick reads as an
        // end-cap rather than a separator. Only draw ticks for interior milestones.
        if (pct >= 99 || pct <= 1) {
          d.style.display = 'none';
        } else {
          d.style.display = '';
          d.style.left = `${pct}%`;
        }
      });
      this.els.pcts.forEach((p) => {
        const pct = Math.min(100, (Number(p.dataset.qty) / this.maxTierQty) * 100);
        if (pct >= 98) { p.style.right = '0'; p.style.transform = 'none'; }
        else p.style.left = `${pct}%`;
      });
    }

    // Match the stepper height to the "Add to bundle" button so swapping them never shifts layout.
    measureAddHeight() {
      const btn = this.querySelector('.bundle-builder__add');
      if (btn && btn.offsetHeight) this.style.setProperty('--bb-add-h', `${btn.offsetHeight}px`);
    }

    // ---- sticky mobile summary (opt-in via settings.sticky_summary_mobile) ----
    measureBarHeight() {
      const bar = this.els.mobilebar;
      if (bar && bar.offsetHeight) this.style.setProperty('--bb-bar-h', `${bar.offsetHeight}px`);
    }

    // Only show the bar while the section is in view; toggling on intersection also
    // means two bundle sections never show two bars at once.
    setupBarObserver() {
      if (!('IntersectionObserver' in window)) { if (this._portal) this._portal.classList.add('bb-bar-active'); return; }
      this._barObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (this._portal.classList.contains('is-sheet-open')) return;
          this._portal.classList.toggle('bb-bar-active', entry.isIntersecting);
          if (entry.isIntersecting) this.updateBarDock();
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      this._barObserver.observe(this);
    }

    // Pin the bar to the viewport bottom while scrolling through the section, then let
    // it come to rest at the section's end so it never overlaps the sections below.
    // The bar is portaled to <body>. Rather than repositioning a fixed element every
    // scroll frame (which relayouts it and jerks on mobile), we flip it once at the
    // crossover: while pinned it stays position:fixed; when the section's bottom reaches
    // the viewport bottom we freeze the bar's current spot in document coordinates and
    // switch to position:absolute, so from then on it scrolls WITH the page natively.
    // No per-frame writes = smooth scrolling in both states.
    updateBarDock() {
      const bar = this.els.mobilebar;
      if (!bar || !this._portal) return;
      if (this._portal.classList.contains('is-sheet-open')) return;
      // While hidden, leave the bar as-is so we never fight its fade-out.
      if (!this._portal.classList.contains('bb-bar-active')) return;
      const reached = this.getBoundingClientRect().bottom <= window.innerHeight;
      if (reached === this._barDocked) return;
      this._barDocked = reached;
      if (reached) {
        // Freeze at the current on-screen position (document coords) and let it scroll.
        const top = window.scrollY + bar.getBoundingClientRect().top;
        bar.style.position = 'absolute';
        bar.style.top = `${top}px`;
        bar.style.bottom = 'auto';
      } else {
        // Back to viewport-pinned (revert to the CSS fixed rule).
        bar.style.position = '';
        bar.style.top = '';
        bar.style.bottom = '';
      }
    }

    toggleSheet() {
      if (this._portal && this._portal.classList.contains('is-sheet-open')) this.closeSheet();
      else this.openSheet();
    }

    openSheet() {
      if (this._panelReturnT) clearTimeout(this._panelReturnT);
      // Move the panel into the portal so the bottom sheet is viewport-fixed.
      if (this.els.panel && this._portal && this.els.panel.parentNode !== this._portal) {
        this._portal.appendChild(this.els.panel);
        void this._portal.offsetWidth; // reflow so translateY(100%) applies before animating up
      }
      this._portal.classList.add('is-sheet-open');
      if (this.els.mobilebar) this.els.mobilebar.setAttribute('aria-expanded', 'true');
      document.body.classList.add('bb-sheet-lock');
      if (this.els.sheetClose) this.els.sheetClose.focus();
    }

    closeSheet(returnFocus = true) {
      if (this._portal) this._portal.classList.remove('is-sheet-open');
      if (this.els.mobilebar) {
        this.els.mobilebar.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.els.mobilebar.focus();
      }
      document.body.classList.remove('bb-sheet-lock');
      // Return the panel to its in-grid home after the slide-out animation, so the
      // desktop sticky rail and the mobile hidden state stay correct.
      if (this._panelReturnT) clearTimeout(this._panelReturnT);
      this._panelReturnT = setTimeout(() => {
        if (this._portal && !this._portal.classList.contains('is-sheet-open') && this.els.panel && this._panelHome) {
          this._panelHome.parent.insertBefore(this.els.panel, this._panelHome.next);
        }
      }, 360);
    }

    bindControls() {
      this.rows.forEach((row) => {
        const add = row.querySelector('[data-bundle-toggle]');
        if (add && !add.disabled) add.addEventListener('click', () => this.setQty(row, 1));
        const dec = row.querySelector('[data-bb-dec]');
        const inc = row.querySelector('[data-bb-inc]');
        if (dec) dec.addEventListener('click', () => this.setQty(row, this.qtyOf(row) - 1));
        if (inc) inc.addEventListener('click', () => this.setQty(row, this.qtyOf(row) + 1));
      });
    }

    // Sets a product's quantity in the bundle (0 = not in the bundle).
    setQty(row, n) {
      n = Math.max(0, Math.round(n));
      // Cap the bundle's TOTAL quantity when a maximum is set.
      if (this.cfg.maxItems > 0 && n > this.qtyOf(row)) {
        const others = this.totalQty() - this.qtyOf(row);
        if (others + n > this.cfg.maxItems) n = Math.max(this.qtyOf(row), this.cfg.maxItems - others);
      }
      row.dataset.qty = n;
      row.classList.toggle('is-active', n > 0);
      this.els.alert.hidden = true;
      this.render();
      this.saveState();
    }

    // Swaps the card between the "Add to bundle" button and the quantity stepper.
    updateRowControls(row) {
      const n = this.qtyOf(row);
      const add = row.querySelector('[data-bundle-toggle]');
      const stepper = row.querySelector('[data-bundle-stepper]');
      const val = row.querySelector('[data-bb-qty]');
      if (val) val.textContent = n;
      if (add) add.hidden = n > 0;
      if (stepper) stepper.hidden = n === 0;
    }

    // Applies a chosen variant to a row (used by the panel's variant dropdown and by state restore).
    applyVariant(row, variant) {
      row.dataset.variantId = variant.id;
      row.dataset.price = variant.price;
      row.dataset.variantTitle = variant.title || '';
      if (variant.image) row.dataset.image = variant.image;
      row.querySelectorAll('.price .price-item--regular, .price .price-item--sale').forEach((el) => {
        el.textContent = this.money(variant.price);
      });
      this.render();
      this.saveState();
    }

    caretSvg() {
      return '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708z" fill="currentColor"/></svg>';
    }

    variantSelect(row) {
      const wrap = document.createElement('div');
      wrap.className = 'bundle-builder__item-select';
      const select = document.createElement('select');
      select.className = 'bundle-builder__item-select__field';
      select.setAttribute('aria-label', row.dataset.title);
      row._variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.available === false ? `${v.title} - ${this.str.soldOut || 'Sold out'}` : v.title;
        if (v.available === false) opt.disabled = true;
        if (String(v.id) === String(row.dataset.variantId)) opt.selected = true;
        select.appendChild(opt);
      });
      select.addEventListener('change', () => {
        const v = row._variants.find((x) => String(x.id) === select.value);
        if (v) this.applyVariant(row, v);
      });
      wrap.appendChild(select);
      wrap.insertAdjacentHTML('beforeend', this.caretSvg());
      return wrap;
    }

    renderItems(tier) {
      this.els.items.querySelectorAll('.bundle-builder__item').forEach((n) => n.remove());
      const active = this.rows.filter((r) => this.qtyOf(r) > 0);
      if (this.els.skeleton) this.els.skeleton.hidden = active.length > 0;
      active.forEach((r) => {
        const q = this.qtyOf(r);
        const unit = Number(r.dataset.price);
        const li = document.createElement('li');
        li.className = 'bundle-builder__item';

        const img = document.createElement('img');
        img.className = 'bundle-builder__item-image';
        img.src = r.dataset.image; img.alt = ''; img.width = 40; img.height = 40; img.loading = 'lazy';

        const main = document.createElement('div');
        main.className = 'bundle-builder__item-main';
        const name = document.createElement('span');
        name.className = 'bundle-builder__item-name';
        name.textContent = r.dataset.title;

        const meta = document.createElement('span');
        meta.className = 'bundle-builder__item-meta';
        if (q > 1) {
          const qs = document.createElement('span');
          qs.className = 'bundle-builder__item-qty';
          qs.textContent = `${q} × `;
          meta.appendChild(qs);
        }
        if (tier) {
          const oldP = document.createElement('s');
          oldP.className = 'bundle-builder__item-old';
          oldP.textContent = this.money(unit);
          const newP = document.createElement('span');
          newP.className = 'bundle-builder__item-new';
          newP.textContent = this.money(unit - Math.round(unit * tier.percentage / 100));
          meta.append(oldP, ' ', newP);
        } else {
          meta.append(document.createTextNode(this.money(unit)));
        }
        main.append(name, meta);

        // Variant picker lives here, in the panel — not on the product card.
        if (r._variants && r._variants.length > 1) {
          main.appendChild(this.variantSelect(r));
        }

        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'bundle-builder__item-remove';
        rm.setAttribute('aria-label', `${this.str.remove || 'Remove'}: ${r.dataset.title}`);
        rm.textContent = '✕';
        rm.addEventListener('click', () => this.setQty(r, 0));

        li.append(img, main, rm);
        this.els.items.appendChild(li);
      });
    }

    render() {
      // Progress is driven by the TOTAL quantity of products in the bundle.
      const count = this.totalQty();
      const tier = this.tierFor(count);

      const pct = `${Math.min(100, (count / this.maxTierQty) * 100)}%`;
      if (this.els.fill) this.els.fill.style.width = pct;
      this.els.ticks.forEach((d) => d.classList.toggle('is-hit', count >= Number(d.dataset.qty)));
      this.els.pcts.forEach((p) => p.classList.toggle('is-hit', count >= Number(p.dataset.qty)));

      // Keep every card's control (button vs stepper) in sync with its quantity.
      this.rows.forEach((r) => this.updateRowControls(r));

      // Totals — gross vs discounted (the real discount is applied by Shopify at checkout).
      let gross = 0, net = 0;
      this.rows.forEach((r) => {
        const q = this.qtyOf(r);
        if (!q) return;
        const line = Number(r.dataset.price) * q;
        gross += line;
        net += tier ? line - Math.round(line * tier.percentage / 100) : line;
      });
      const savings = gross - net;
      if (this.els.totals) this.els.totals.hidden = count === 0;
      if (this.els.totalAmount) this.els.totalAmount.textContent = this.money(net);
      if (this.els.savings) {
        if (savings > 0) {
          this.els.savings.hidden = false;
          if (this.els.savingsAmount) this.els.savingsAmount.textContent = this.money(savings);
        } else {
          this.els.savings.hidden = true;
        }
      }

      // Status message — updates for the milestone currently reached / being approached.
      if (this.els.status && this.tiers.length) {
        this.els.status.classList.toggle('is-unlocked', !!tier);
        const next = this.tiers.find((tr) => tr.quantity > count);
        if (count === 0) {
          this.els.status.textContent = this.str.saveUpTo || '';
        } else if (tier) {
          this.els.status.textContent = next
            ? this.t(this.str.haveDiscount, { percentage: tier.percentage, count: next.quantity - count, nextPercentage: next.percentage })
            : this.t(this.str.unlockedMax, { percentage: tier.percentage });
        } else if (next) {
          this.els.status.textContent = this.t(this.str.addToUnlock, { count: next.quantity - count, percentage: next.percentage });
        }
      }

      this.els.cta.disabled = count === 0;
      this.renderItems(tier);

      if (this.stickyMobile && this.els.mobilebar) {
        if (this.els.mobilebarFill) this.els.mobilebarFill.style.width = pct;
        if (this.els.mobilebarHint && this.els.status) this.els.mobilebarHint.textContent = this.els.status.textContent;
        this.updateBarDock();
      }
    }

    // ---- state persistence (keeps selections when the editor re-renders the section) ----
    stateKey() { return `bundle-builder:${this.dataset.sectionId}`; }
    saveState() {
      if (!this.designMode) return;
      try {
        const map = {};
        this.rows.forEach((r) => {
          const q = this.qtyOf(r);
          if (q > 0) map[r.dataset.productId] = { variantId: r.dataset.variantId, qty: q };
        });
        sessionStorage.setItem(this.stateKey(), JSON.stringify(map));
      } catch (e) { /* storage unavailable */ }
    }
    restoreState() {
      if (!this.designMode) return;
      let map = null;
      try { map = JSON.parse(sessionStorage.getItem(this.stateKey()) || 'null'); } catch (e) { map = null; }
      if (!map) return;
      this.rows.forEach((r) => {
        const saved = map[r.dataset.productId];
        if (!saved) return;
        if (r._variants && saved.variantId) {
          const v = r._variants.find((x) => String(x.id) === String(saved.variantId));
          if (v) this.applyVariant(r, v);
        }
        this.setQty(r, saved.qty || 1);
      });
    }

    async addBundle() {
      const cta = this.els.cta;
      const original = cta.textContent;
      cta.disabled = true;
      cta.textContent = this.str.adding || 'Adding…';
      this.els.alert.hidden = true;

      const items = this.rows
        .filter((r) => this.qtyOf(r) > 0)
        .map((r) => ({ id: Number(r.dataset.variantId), quantity: this.qtyOf(r) }));

      // Mirror the theme's own add-to-cart flow: ask /cart/add.js to render the
      // cart sections in the same request, then hand the response to whichever
      // cart UI the theme uses (renderContents also opens the drawer/notification).
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items, sections_url: window.location.pathname };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
      } else {
        body.sections = ['cart-icon-bubble'];
      }

      try {
        const res = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const response = await res.json();
        if (!res.ok || response.status) {
          throw new Error(response.description || this.str.error || 'Error');
        }

        // Follow the theme's Cart type setting: 'page' navigates to the cart, otherwise
        // the drawer / notification is re-rendered (and opened) in place.
        if (this.cfg.cartType === 'page') {
          window.location.href = `${this.root}cart`;
          return;
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
          // The theme's cart-drawer keeps its `is-empty` class (set by Liquid at page
          // load) after renderContents, which hides items added from a previously empty
          // cart. We just added items, so clear it to reveal the updated cart.
          cart.classList.remove('is-empty');
        } else if (response.sections && response.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            const parsed = new DOMParser()
              .parseFromString(response.sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section');
            if (parsed) bubble.innerHTML = parsed.innerHTML;
          }
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder' }); } catch (e) { /* noop */ }
        }

        if (this.stickyMobile) this.closeSheet(false);
        cta.textContent = this.str.added || 'Added ✓';
        this.rows.forEach((r) => this.setQty(r, 0));
        setTimeout(() => { cta.textContent = original; cta.disabled = this.totalQty() === 0; }, 1500);
      } catch (err) {
        this.els.alert.textContent = err.message || this.str.error;
        this.els.alert.hidden = false;
        cta.textContent = original;
        cta.disabled = false;
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
